import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Space,
  Typography,
  Card,
  Tag,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { toastError, toastSuccess } from "../../../utils/toast";
import { fetchFaqs, createFaq, updateFaq, deleteFaq } from "../../../api/faqsApi";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function FaqsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchFaqs();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toastError("Failed to fetch FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const showCreateModal = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, display_order: 0 });
    setModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      question: record.question,
      answer: record.answer,
      display_order: record.display_order || 0,
      is_active: Number(record.is_active) === 1,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        question: values.question.trim(),
        answer: values.answer.trim(),
        display_order: values.display_order ?? 0,
        is_active: values.is_active ? 1 : 0,
      };

      setSaving(true);
      if (editing) {
        await updateFaq(editing.id, payload);
        toastSuccess("FAQ updated");
      } else {
        await createFaq(payload);
        toastSuccess("FAQ created");
      }
      setModalVisible(false);
      load();
    } catch (err) {
      if (!err.errorFields) toastError("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFaq(id);
      toastSuccess("FAQ deleted");
      load();
    } catch {
      toastError("Delete failed");
    }
  };

  const columns = [
    {
      title: "Question",
      dataIndex: "question",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Answer",
      dataIndex: "answer",
      ellipsis: true,
    },
    {
      title: "Order",
      dataIndex: "display_order",
      width: 90,
      sorter: (a, b) => (a.display_order || 0) - (b.display_order || 0),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      width: 110,
      render: (isActive) => {
        const active = Number(isActive) === 1;
        return (
          <Tag icon={active ? <CheckOutlined /> : <CloseOutlined />} color={active ? "success" : "error"}>
            {active ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            type="text"
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => showEditModal(record)}
          />
          <Popconfirm title="Delete this FAQ?" onConfirm={() => handleDelete(record.id)} okText="Delete">
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={4} style={{ margin: 0 }}>
              FAQs
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
              New FAQ
            </Button>
          </div>
        }
        bordered={false}
      >
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          bordered
        />
      </Card>

      <Modal
        title={editing ? "Edit FAQ" : "New FAQ"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="question" label="Question" rules={[{ required: true, min: 4 }]}>
            <Input placeholder="How does Click Law work?" />
          </Form.Item>
          <Form.Item name="answer" label="Answer" rules={[{ required: true, min: 4 }]}>
            <TextArea rows={5} placeholder="Write a clear answer for website visitors." />
          </Form.Item>
          <Form.Item name="display_order" label="Display order">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
